<template>
  <component :is="tag" :class="classes" :style="styles" class="ui-text">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'subtitle-1'
  | 'subtitle-2'
  | 'subtitle-3'
  | 'subtitle-4'
  | 'subtitle-5'
  | 'subtitle-6'
  | 'body-1'
  | 'body-2'
  | 'body-3'
  | 'body-4'
  | 'link-1'
  | 'body-cross';

type Tag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';

export interface Props {
  variant?: Variant;
  color?: string;
  tag?: Tag;
  lineClamp?: number | undefined;
  truncate?: boolean;
  lineHeight?: number | string;
  muted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'body-1',
  tag: 'p',
  lineClamp: undefined,
  truncate: false,
  lineHeight: undefined,
  muted: false,
  color: undefined,
});

const classes = computed(() => [
  `text-${props.variant}`,
  { 'text-truncate': props.truncate },
  props.lineClamp !== undefined && 'ui-text__truncated',
]);
const styles = computed(() => [
  props.lineClamp !== null && `-webkit-line-clamp: ${props.lineClamp};`,
  props.lineHeight !== undefined && `line-height: ${props.lineHeight}px`,
  props.muted && 'opacity: 0.7',
]);

const textColor = computed(() => {
  if (props.color === undefined || props.color === 'inherit') return 'inherit';
  if (props.color === 'currentColor') return 'currentColor';

  return `rgb(var(--v-theme-${props.color}))`;
});
</script>

<style scoped lang="scss">
.ui-text {
  color: v-bind(textColor);
}

.ui-text__truncated {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal !important;
}
</style>
